interface BookingContext {
  agentUserId: string
  eventTypeId: string
  eventTypeSlug: string
  slot: {
    startTime: number
    endTime: number
    durationMinutes: number
  }
  timezone: string
  prefillValues: {
    email: string
    name: string
  }
}

interface BookingContextMessage {
  type: 'qc-booking-context'
  source: 'book.quickcasa.ai'
  protocolVersion: 1
  context: BookingContext
}

interface BookingResizeMessage {
  type: 'qc-booking-resize'
  source: 'book.quickcasa.ai'
  protocolVersion: 1
  height: number
}

interface FormReadyMessage {
  type: 'qc-form-ready'
  source: 'forms.quickcasa.ai'
  protocolVersion: 1
}

interface FormSubmittedMessage {
  type: 'qc-form-submitted'
  source: 'forms.quickcasa.ai'
  protocolVersion: 1
  submission: {
    formId: string
    responses: Record<string, unknown>
    extractedName: string
    extractedEmail: string
    extractedPhone: string
    turnstileToken: string
  }
}

interface FormErrorMessage {
  type: 'qc-form-error'
  source: 'forms.quickcasa.ai'
  protocolVersion: 1
  message: string
}

type IncomingFormMessage =
  | FormReadyMessage
  | FormSubmittedMessage
  | FormErrorMessage

const BOOK_SOURCE = 'book.quickcasa.ai'
const FORMS_SOURCE = 'forms.quickcasa.ai'

/**
 * Type guard for messages arriving from the embedded form iframe. Filters out
 * everything that isn't a `qc-form-*` message coming from the forms app —
 * postMessage delivers events from every script on the page (React DevTools,
 * extensions, etc.) so a strict filter is mandatory.
 *
 * @param {unknown} data - The raw `MessageEvent.data` payload.
 * @returns {data is IncomingFormMessage} True when this is a valid form message.
 */
const isIncomingFormMessage = (
  data: unknown,
): data is IncomingFormMessage => {
  if (!data || typeof data !== 'object') {
    return false
  }
  const candidate = data as Record<string, unknown>
  if (candidate.source !== FORMS_SOURCE) {
    return false
  }
  if (candidate.protocolVersion !== 1) {
    return false
  }
  return (
    candidate.type === 'qc-form-ready' ||
    candidate.type === 'qc-form-submitted' ||
    candidate.type === 'qc-form-error'
  )
}

/**
 * Sends the booking context to the embedded form iframe. Called when the form
 * iframe signals `qc-form-ready` so we know it's listening.
 *
 * @param {Window} targetWindow - The iframe's contentWindow.
 * @param {BookingContext} context - All booking-state to forward.
 */
const postBookingContext = (
  targetWindow: Window,
  context: BookingContext,
): void => {
  const message: BookingContextMessage = {
    type: 'qc-booking-context',
    source: BOOK_SOURCE,
    protocolVersion: 1,
    context,
  }
  targetWindow.postMessage(message, '*')
}

/**
 * Posts the booking page's current content height to the host window so an
 * embedding iframe can auto-resize to fit (no inner scrollbar). Only fires when
 * actually framed; a no-op on the standalone page.
 *
 * @param {number} height - The content height in pixels.
 */
const postBookingHeightToParent = (height: number): void => {
  if (window.parent === window) {
    return
  }
  const message: BookingResizeMessage = {
    type: 'qc-booking-resize',
    source: BOOK_SOURCE,
    protocolVersion: 1,
    height,
  }
  window.parent.postMessage(message, '*')
}

export {
  isIncomingFormMessage,
  postBookingContext,
  postBookingHeightToParent,
  BOOK_SOURCE,
  FORMS_SOURCE,
}
export type {
  BookingContext,
  BookingContextMessage,
  BookingResizeMessage,
  FormReadyMessage,
  FormSubmittedMessage,
  FormErrorMessage,
  IncomingFormMessage,
}
